/** /tour-operators/configs — batch tenant configuration.
 *
 * One row per canonical config key (schema-defined + observed), aggregated
 * across every tenant's config in memory. Expanding a row shows who defines
 * the key with what values; the manage permission unlocks inline cross-tenant
 * editing into a page-lifetime change-set store (undo/redo, pending drawer)
 * that persists through ONE all-or-nothing batch request.
 *
 * Deliberately no `useGridPresets`: rows are client-derived (no server
 * ordering/paging to capture) and the filter bar lives in plain URL search
 * params, which already gives shareable links.
 */
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGridPro, type GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { BatchDataProvider, type BatchData } from './BatchDataContext';
import {
  ChangeSetProvider,
  useCanUndoRedo,
  useChangeSet,
  usePendingByCanonicalKey,
  usePendingTotals,
} from './ChangeSetContext';
import { buildColumns } from './columns';
import type { BatchRow } from './configIndex';
import { KeyDetailPanel } from './KeyDetailPanel';
import { matchesKeyQuery } from './pathOps';
import { PendingChangesDrawer } from './PendingChangesDrawer';
import { PersistDialog } from './PersistDialog';
import { useBatchConfigData } from './useBatchConfigData';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'boolean', label: 'boolean' },
  { value: 'integer', label: 'integer' },
  { value: 'number', label: 'number' },
  { value: 'string', label: 'string' },
  { value: 'enum', label: 'enum' },
  { value: 'array', label: 'array' },
  { value: 'object', label: 'object' },
  { value: 'secret', label: 'secret' },
];

const DEFINED_OPTIONS = [
  { value: 'all', label: 'All keys' },
  { value: 'somewhere', label: 'Defined somewhere' },
  { value: 'nowhere', label: 'Not defined anywhere' },
  { value: 'everywhere', label: 'Defined everywhere' },
];

function rowMatchesType(row: BatchRow, type: string): boolean {
  if (type === 'secret') return row.secret;
  if (type === 'enum') return Boolean(row.schema?.enum);
  return (row.schema?.type ?? row.type) === type;
}

export function BatchConfigPage() {
  // The change-set store lives INSIDE the page component so pending edits
  // die with the page (the nav guard is the only exit toll).
  return (
    <ChangeSetProvider>
      <BatchConfigPageContent />
    </ChangeSetProvider>
  );
}

function BatchConfigPageContent() {
  const { schemaQuery, configsQuery, rows, index, tenantById, tenants, refetchConfigs } =
    useBatchConfigData();
  const { dispatch } = useChangeSet();
  const totals = usePendingTotals();
  const pendingByKey = usePendingByCanonicalKey();
  const { canUndo, canRedo } = useCanUndoRedo();

  const [sp, setSp] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [persistOpen, setPersistOpen] = useState(false);

  const q = sp.get('q') ?? '';
  const typeFilter = sp.get('type') ?? 'all';
  const definedFilter = sp.get('defined') ?? 'all';
  const pendingOnly = sp.get('pending') === '1';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );

  // Debounced key search — the TextField is instant, the URL (and therefore
  // the row filter) follows ~200 ms behind.
  const [qInput, setQInput] = useState(q);
  useEffect(() => {
    if (qInput === q) return undefined;
    const t = setTimeout(() => setParam('q', qInput.trim() ? qInput : null), 200);
    return () => clearTimeout(t);
  }, [qInput, q, setParam]);

  // Page-level undo/redo shortcuts. Skipped while typing in any input so the
  // browser's native text-field undo keeps working.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatch]);

  const { prompt } = useUnsavedChangesPrompt(totals.edits > 0, {
    message:
      'You have pending batch config edits that have not been persisted. Leaving now will discard them.',
  });

  const tenantCount = tenants.length;

  const batchData = useMemo<BatchData>(
    () => ({ index, tenantById, tenantCount, refetchConfigs }),
    [index, tenantById, tenantCount, refetchConfigs],
  );

  const secretKeys = useMemo(
    () => new Set(rows.filter((r) => r.secret).map((r) => r.id)),
    [rows],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (q && !matchesKeyQuery(q, row.key, row.description)) return false;
        if (typeFilter !== 'all' && !rowMatchesType(row, typeFilter)) return false;
        if (definedFilter === 'somewhere' && row.definedCount === 0) return false;
        if (definedFilter === 'nowhere' && row.definedCount !== 0) return false;
        if (definedFilter === 'everywhere' && row.definedCount !== tenantCount) return false;
        if (pendingOnly && (pendingByKey.get(row.id) ?? 0) === 0) return false;
        return true;
      }),
    [rows, q, typeFilter, definedFilter, pendingOnly, pendingByKey, tenantCount],
  );

  const columns = useMemo(() => buildColumns(tenantCount), [tenantCount]);

  // MUST stay referentially stable — panels pull live data from contexts, a
  // changing callback would remount every open panel on each edit.
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<BatchRow>) => <KeyDetailPanel row={row} />,
    [],
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const loading = schemaQuery.isLoading || configsQuery.isLoading;
  const loadError = schemaQuery.error ?? configsQuery.error;

  return (
    <BatchDataProvider value={batchData}>
      <Box sx={PAGE_FILL_SX}>
        <PageHeader
          helpStory="top-batch-config"
          title="Batch Configuration"
          subtitle="Every config key across every tenant — inspect value spreads and edit many operators at once."
          actions={
            <PermissionGate perm="console_top_manage">
              <Badge badgeContent={totals.edits} color="warning">
                <Button
                  variant="contained"
                  size="small"
                  disabled={totals.edits === 0}
                  onClick={() => setPersistOpen(true)}
                >
                  Persist changes…
                </Button>
              </Badge>
            </PermissionGate>
          }
        />

        <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search keys…  plain text, or a path like auth_methods[*].domain"
            sx={{ width: 380 }}
          />
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setParam('type', e.target.value === 'all' ? null : e.target.value)}
            sx={{ width: 150 }}
          >
            {TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Defined"
            value={definedFilter}
            onChange={(e) => setParam('defined', e.target.value === 'all' ? null : e.target.value)}
            sx={{ width: 200 }}
          >
            {DEFINED_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={pendingOnly}
                onChange={(e) => setParam('pending', e.target.checked ? '1' : null)}
              />
            }
            label="Pending only"
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Undo (Ctrl/Cmd+Z)">
            <span>
              <IconButton size="small" disabled={!canUndo} onClick={() => dispatch({ type: 'UNDO' })}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Ctrl/Cmd+Shift+Z)">
            <span>
              <IconButton size="small" disabled={!canRedo} onClick={() => dispatch({ type: 'REDO' })}>
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => setDrawerOpen(true)}>
            Pending changes ({totals.edits})
          </Button>
        </Stack>

        {loadError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void schemaQuery.refetch();
                  void configsQuery.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Couldn’t load tenant configurations:{' '}
            {loadError instanceof Error ? loadError.message : String(loadError)}
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGridPro<BatchRow>
            rows={filteredRows}
            getRowId={(r) => r.id}
            columns={columns}
            density="compact"
            pagination
            initialState={{ pagination: { paginationModel: { pageSize: 250, page: 0 } } }}
            pageSizeOptions={[100, 250, 500]}
            disableColumnFilter
            disableRowSelectionOnClick
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            sx={{ flex: 1, minHeight: 360 }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 4, textAlign: 'center' }}>No config keys match these filters.</Box>
              ),
            }}
          />
        )}

        <PendingChangesDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onPersist={() => setPersistOpen(true)}
          secretKeys={secretKeys}
        />
        <PersistDialog
          open={persistOpen}
          onClose={() => setPersistOpen(false)}
          secretKeys={secretKeys}
        />

        {prompt}
      </Box>
    </BatchDataProvider>
  );
}
